export const CONTAINER_DEFAULTS = Object.freeze({ version: 1, layout: 'stack', gap: 'medium', align: 'stretch', justify: 'start', wrap: true, stackOnMobile: true, columnCount: null, minColumnWidth: '16rem', backgroundColor: null, borderColor: null, borderWidth: 0 });
export const CONTAINER_LAYOUTS = Object.freeze(['group', 'row', 'stack', 'grid']);
export const CONTAINER_GAPS = Object.freeze(['none', 'xs', 'sm', 'medium', 'lg', 'xl']);
export const CONTAINER_ALIGNS = Object.freeze(['start', 'center', 'end', 'stretch']);
export const CONTAINER_JUSTIFY = Object.freeze(['start', 'center', 'end', 'space-between']);
export const CONTAINER_COLUMN_COUNTS = Object.freeze([2, 3, 4]);
export const CONTAINER_MIN_WIDTHS = Object.freeze(['12rem', '16rem', '20rem']);
export const COLUMN_DEFAULTS = Object.freeze({ width: 'auto', grow: true, verticalAlign: 'start', mobileWidth: 'full', backgroundColor: null, borderColor: null, borderWidth: 0 });
export const COLUMN_WIDTHS = Object.freeze(['auto', '1/4', '1/3', '1/2', '2/3', '3/4', 'full']);
export const COLUMN_VERTICAL_ALIGNS = Object.freeze(['start', 'center', 'end', 'stretch']);
export const COLUMN_MOBILE_WIDTHS = Object.freeze(['auto', 'full']);

const allowed = (value, values, fallback) => values.includes(value) ? value : fallback;
const normalizedGap = (value) => value === 'md' ? 'medium' : value;
const normalizedColor = (value) => typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
const normalizedBorderWidth = (value) => { const width = Number(value); return [0, 1, 2, 4].includes(width) ? width : 0; };
const preservedCommonAttrs = (attrs = {}) => Object.fromEntries(['builderId','builderPlaceholder','layoutSizing','layoutWidth','builderSettings','styleSettings'].filter((key) => key in attrs).map((key) => [key,attrs[key]]));

export function normalizeContainerAttrs(attrs = {}) {
  const count = Number(attrs.columnCount);
  return {
    ...preservedCommonAttrs(attrs),
    version: 1,
    layout: allowed(attrs.layout, CONTAINER_LAYOUTS, CONTAINER_DEFAULTS.layout),
    gap: allowed(normalizedGap(attrs.gap), CONTAINER_GAPS, CONTAINER_DEFAULTS.gap),
    align: allowed(attrs.align, CONTAINER_ALIGNS, CONTAINER_DEFAULTS.align),
    justify: allowed(attrs.justify, CONTAINER_JUSTIFY, CONTAINER_DEFAULTS.justify),
    wrap: typeof attrs.wrap === 'boolean' ? attrs.wrap : CONTAINER_DEFAULTS.wrap,
    stackOnMobile: typeof attrs.stackOnMobile === 'boolean' ? attrs.stackOnMobile : CONTAINER_DEFAULTS.stackOnMobile,
    columnCount: CONTAINER_COLUMN_COUNTS.includes(count) ? count : null,
    minColumnWidth: allowed(attrs.minColumnWidth, CONTAINER_MIN_WIDTHS, CONTAINER_DEFAULTS.minColumnWidth),
    backgroundColor: normalizedColor(attrs.backgroundColor),
    borderColor: normalizedColor(attrs.borderColor),
    borderWidth: normalizedBorderWidth(attrs.borderWidth),
  };
}
export function normalizeColumnWidth(value) { const legacy = { '1fr': 'auto', '25%': '1/4', '33.333%': '1/3', '33.3333%': '1/3', '50%': '1/2', '66.667%': '2/3', '66.6667%': '2/3', '75%': '3/4', '100%': 'full' }; return allowed(legacy[value] || value, COLUMN_WIDTHS, COLUMN_DEFAULTS.width); }
export function normalizeColumnAttrs(attrs = {}) { return { ...preservedCommonAttrs(attrs), width: normalizeColumnWidth(attrs.width), grow: typeof attrs.grow === 'boolean' ? attrs.grow : COLUMN_DEFAULTS.grow, verticalAlign: allowed(attrs.verticalAlign, COLUMN_VERTICAL_ALIGNS, COLUMN_DEFAULTS.verticalAlign), mobileWidth: allowed(attrs.mobileWidth, COLUMN_MOBILE_WIDTHS, COLUMN_DEFAULTS.mobileWidth), backgroundColor: normalizedColor(attrs.backgroundColor), borderColor: normalizedColor(attrs.borderColor), borderWidth: normalizedBorderWidth(attrs.borderWidth) }; }
export function columnPresentation(attrs = {}) { const value = normalizeColumnAttrs(attrs); return { attrs: value, className: 'mts-column', dataAttrs: { 'data-width': value.width, 'data-grow': String(value.grow), 'data-vertical-align': value.verticalAlign, 'data-mobile-width': value.mobileWidth } }; }

function gapFromLegacyRow(attrs = {}) {
  if (attrs.gap) return normalizedGap(attrs.gap);
  const pixels = Number(attrs.columnGap);
  if (!Number.isFinite(pixels)) return CONTAINER_DEFAULTS.gap;
  if (pixels <= 0) return 'none';
  if (pixels <= 4) return 'xs';
  if (pixels <= 8) return 'sm';
  if (pixels <= 16) return 'medium';
  if (pixels <= 24) return 'lg';
  return 'xl';
}

export function hasMeaningfulColumnSizing(attrs = {}) {
  if (attrs.width != null && !['', '1fr', 'auto', '100%'].includes(String(attrs.width))) return true;
  if (attrs.basis != null && !['', 'auto', '1fr', '100%'].includes(String(attrs.basis))) return true;
  if (attrs.span != null && Number(attrs.span) !== 1) return true;
  return ['columnSpan', 'gridColumn', 'responsive', 'responsiveSizing', 'mobileWidth', 'tabletWidth', 'desktopWidth', 'minWidth', 'maxWidth'].some((key) => attrs[key] != null && attrs[key] !== '' && attrs[key] !== false);
}

export function getContainerLayoutMode(node = {}) {
  if (node?.type === 'container' && Array.isArray(node.content) && node.content.length > 0 && node.content.every((child) => child?.type === 'column')) return 'columns';
  return normalizeContainerAttrs(node?.attrs || node).layout;
}

function canonicalizeContainer(node) {
  const children = Array.isArray(node.content) ? node.content : [];
  const columnsMode = children.length > 0 && children.every((child) => child?.type === 'column');
  const content = [];
  for (let index = 0; index < children.length;) {
    if (columnsMode || children[index]?.type !== 'column') { content.push(children[index]); index += 1; continue; }
    const columns = [];
    while (index < children.length && children[index]?.type === 'column') { columns.push(children[index]); index += 1; }
    content.push({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'row' }), content: columns });
  }
  const attrs = normalizeContainerAttrs(node.attrs || {});
  return { ...node, attrs: { ...(node.attrs || {}), ...attrs }, content };
}

export function normalizeContainerDocument(value) {
  const source = value == null ? value : JSON.parse(JSON.stringify(value));
  const migrate = (node) => {
    if (!node || typeof node !== 'object') return node;
    const children = Array.isArray(node.content) ? node.content.map(migrate) : [];
    if (node.type === 'layoutColumn' || node.type === 'column') {
      return { type: 'column', attrs: { ...(node.attrs || {}), ...normalizeColumnAttrs(node.attrs) }, content: children };
    }
    if (node.type === 'layoutRow') {
      const rowAttrs = node.attrs || {};
      const content = children;
      return { type: 'container', attrs: { ...rowAttrs, ...normalizeContainerAttrs({ layout: 'row', gap: gapFromLegacyRow(rowAttrs), align: rowAttrs.align, justify: rowAttrs.justify, wrap: rowAttrs.wrap, stackOnMobile: rowAttrs.stackOnMobile ?? rowAttrs.mobileStacking }) }, content };
    }
    if (node.type === 'container') {
      let attrs = { ...(node.attrs || {}) };
      const content = [];
      for (let index = 0; index < children.length; index += 1) {
        const child = children[index];
        if (child?.type === 'container' && child.attrs?.layout === 'row' && node.content?.[index]?.type === 'layoutRow') {
          attrs = { ...attrs, ...child.attrs, layout: 'row' };
          content.push(...(child.content || []));
        } else content.push(child);
      }
      return canonicalizeContainer({ ...node, attrs, content });
    }
    return Array.isArray(node.content) ? { ...node, content: children } : node;
  };
  if (source?.type === 'doc') {
    const migrated = migrate(source);
    const content = [];
    const appendRootStack = (child) => {
      const previous = content.at(-1);
      const directStack = previous?.type === 'container' && previous.attrs?.layout === 'stack' && !(previous.content || []).some((item) => item?.type === 'column');
      if (directStack) previous.content = [...(previous.content || []), child];
      else content.push({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'stack' }), content: [child] });
    };
    (migrated.content || []).forEach((child) => {
      if (child?.type === 'container') {
        if (getContainerLayoutMode(child) === 'columns') content.push({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'stack' }), content: [child] });
        else content.push(child);
        return;
      }
      if (child?.type !== 'column') { appendRootStack(child); return; }
      const previous = content.at(-1);
      const lastChild = previous?.content?.at(-1);
      if (previous?.type === 'container' && ['stack', 'row'].includes(previous.attrs?.layout) && getContainerLayoutMode(lastChild) === 'columns') lastChild.content = [...lastChild.content, child];
      else if (previous?.type === 'container' && ['stack', 'row'].includes(previous.attrs?.layout)) previous.content = [...(previous.content || []), canonicalizeContainer({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'row' }), content: [child] })];
      else content.push({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'stack' }), content: [canonicalizeContainer({ type: 'container', attrs: normalizeContainerAttrs({ layout: 'row' }), content: [child] })] });
    });
    return { ...migrated, content: content.map((child) => child?.type === 'container' ? canonicalizeContainer(child) : child) };
  }
  return migrate(source);
}

export function containerPresentation(attrs = {}) {
  const value = normalizeContainerAttrs(attrs);
  const row = value.layout === 'row';
  const grid = value.layout === 'grid';
  return {
    attrs: value,
    className: ['mts-container', `mts-container--${value.layout}`, `mts-gap--${value.gap}`, (row || grid) ? `mts-container--align-${value.align}` : '', (row || grid) ? `mts-container--justify-${value.justify}` : '', row ? (value.wrap ? 'mts-container--wrap' : 'mts-container--nowrap') : '', (row || grid) && value.stackOnMobile ? 'mts-container--stack-mobile' : '', grid && value.columnCount ? `mts-container--columns-${value.columnCount}` : '', grid && value.minColumnWidth ? `mts-container--min-${value.minColumnWidth.replace('rem', '')}` : ''].filter(Boolean).join(' '),
  };
}

export default { CONTAINER_DEFAULTS, CONTAINER_LAYOUTS, CONTAINER_GAPS, CONTAINER_ALIGNS, CONTAINER_JUSTIFY, CONTAINER_COLUMN_COUNTS, CONTAINER_MIN_WIDTHS, COLUMN_DEFAULTS, COLUMN_WIDTHS, COLUMN_VERTICAL_ALIGNS, COLUMN_MOBILE_WIDTHS, normalizeContainerAttrs, normalizeColumnAttrs, normalizeContainerDocument, getContainerLayoutMode, hasMeaningfulColumnSizing, containerPresentation, columnPresentation };
